import { useEffect, useState, useCallback } from 'react';
import adminApi from '@/services/admin.api';

/** Rows fetched per page — the list is paginated server-side (page/limit query params). */
const PAGE_SIZE = 20;

export interface QASourceItem {
  _id: string;
  title: string;
  questions: string[];
  answer: string;
  createdByName: string;
  updatedByName?: string;
  createdAt: string;
  updatedAt: string;
}

interface UseQASourcesResult {
  items: QASourceItem[];
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;
  /** Current page, 0-indexed (MUI TablePagination convention). */
  page: number;
  setPage: (page: number) => void;
  /** Total rows across all pages, from the response `meta.total`. */
  total: number;
  PAGE_SIZE: number;
  selectedId: string | null;
  form: {
    title: string;
    questions: string[];
    answer: string;
  };
  select: (id: string | null) => void;
  setTitle: (v: string) => void;
  setQuestion: (index: number, value: string) => void;
  addQuestion: () => void;
  removeQuestion: (index: number) => void;
  setAnswer: (v: string) => void;
  resetForm: () => void;
  save: () => Promise<void>;
  remove: (id?: string) => Promise<void>;
  reload: () => Promise<void>;
}

export const useQASources = (): UseQASourcesResult => {
  const [items, setItems] = useState<QASourceItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState<string[]>(['']);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data, meta } = await adminApi.getQASources({ page: page + 1, limit: PAGE_SIZE });
      setItems(data);
      setTotal(meta?.total ?? 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load Q&A sources');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const resetForm = () => {
    setSelectedId(null);
    setTitle('');
    setQuestions(['']);
    setAnswer('');
  };

  const select = (id: string | null) => {
    setError('');
    setSuccess('');
    if (!id) {
      resetForm();
      return;
    }
    const item = items.find((i) => i._id === id);
    if (!item) return;
    setSelectedId(item._id);
    setTitle(item.title);
    setQuestions(item.questions.length ? item.questions : ['']);
    setAnswer(item.answer);
  };

  const setQuestion = (index: number, value: string) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? value : q)));
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, '']);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        title: title.trim(),
        questions: questions.map((q) => q.trim()).filter(Boolean),
        answer: answer.trim(),
      };

      if (!payload.title) throw new Error('Title is required');
      if (!payload.questions.length) throw new Error('At least one question is required');
      if (!payload.answer) throw new Error('Answer is required');

      let saved: QASourceItem;
      if (selectedId) {
        saved = await adminApi.updateQASource(selectedId, payload);
        setItems((prev) => prev.map((i) => (i._id === saved._id ? saved : i)));
      } else {
        saved = await adminApi.createQASource(payload);
        // Server-side pagination: re-read the current page rather than splicing
        // locally, so the page never exceeds PAGE_SIZE rows and `total` stays right.
        await loadList();
        setSelectedId(saved._id);
      }

      setSuccess('Saved successfully');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err: any) {
      setError(err.message || 'Failed to save Q&A');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id?: string) => {
    const targetId = id || selectedId;
    if (!targetId) return;
    try {
      setSaving(true);
      setError('');
      await adminApi.deleteQASource(targetId);
      // If this was the last row on a non-first page, step back — otherwise the
      // user would land on an empty page. Changing `page` re-runs loadList via
      // the effect, so don't also call it here.
      if (items.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        await loadList();
      }
      if (targetId === selectedId) {
        resetForm();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete Q&A');
    } finally {
      setSaving(false);
    }
  };

  return {
    items,
    loading,
    saving,
    error,
    success,
    page,
    setPage,
    total,
    PAGE_SIZE,
    selectedId,
    form: { title, questions, answer },
    select,
    setTitle,
    setQuestion,
    addQuestion,
    removeQuestion,
    setAnswer,
    resetForm,
    save,
    remove,
    reload: loadList,
  };
};
